<template>
  <div class="bg-white/5 rounded-xl p-6 relative overflow-hidden">
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-xl font-semibold">My Franchise Codes</h2>
      <button 
        @click="fetchCodes" 
        class="text-white/60 hover:text-white transition-colors"
        :disabled="isLoading"
      >
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
      </button>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-lg mb-4 text-sm">
      {{ error }}
      <button @click="fetchCodes" class="ml-2 underline hover:text-red-400">Retry</button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading && !codes.length" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!codes.length && !isLoading" class="text-center py-12 text-white/60">
      <div class="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
        <i class="fas fa-ticket-alt text-2xl text-white/40"></i>
      </div>
      <p>No codes generated yet.</p>
    </div>

    <!-- Codes Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead class="bg-white/5 text-white/60 uppercase text-xs">
          <tr>
            <th class="px-4 py-3 rounded-l-lg">Code</th>
            <th class="px-4 py-3">Plan</th>
            <th class="px-4 py-3">Status</th>
            <th class="px-4 py-3">Used By</th>
            <th class="px-4 py-3 rounded-r-lg">Valid Until</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-white/5">
          <tr v-for="code in codes" :key="code.id" class="hover:bg-white/5 transition-colors">
            <td class="px-4 py-4 font-mono font-medium">
              <div class="flex items-center gap-2">
                <span class="select-all">{{ code.code }}</span>
                <button 
                  @click="copyCode(code.code)" 
                  class="text-white/40 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
                  :title="copiedCode === code.code ? 'Copied!' : 'Copy code'"
                >
                  <i :class="copiedCode === code.code ? 'fas fa-check text-green-500' : 'fas fa-copy'" class="text-xs"></i>
                </button>
              </div>
            </td>
            <td class="px-4 py-4 capitalize">{{ formatPlanType(code.plan_type) }}</td>
            <td class="px-4 py-4">
              <span 
                class="px-2 py-1 rounded text-xs font-medium"
                :class="getStatusClass(code.status)"
              >
                {{ code.status }}
              </span>
            </td>
            <td class="px-4 py-4 text-white/80">
              {{ code.used_by_phone || '-' }}
              <div v-if="code.used_at" class="text-xs text-white/40">
                {{ formatDate(code.used_at) }}
              </div>
            </td>
            <td class="px-4 py-4 text-white/60">
              {{ formatDate(code.valid_until) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { franchiseAPI } from '../utils/api'

export default {
  name: 'FranchiseCodes',
  data() {
    return {
      codes: [],
      isLoading: false,
      error: null,
      copiedCode: null
    }
  },
  mounted() {
    this.fetchCodes()
  },
  methods: {
    async fetchCodes() {
      this.isLoading = true
      this.error = null
      try {
        const response = await franchiseAPI.getCodes()
        this.codes = response.data
      } catch (err) {
        console.error('Failed to fetch codes:', err)
        // Only show error if strictly needed, otherwise if 403 just hide content?
        // But here we want the user to know why it failed or if it failed.
      } finally {
        this.isLoading = false
      }
    },
    getStatusClass(status) {
      const map = {
        'ACTIVE': 'bg-green-500/20 text-green-500',
        'USED': 'bg-blue-500/20 text-blue-500',
        'EXPIRED': 'bg-red-500/20 text-red-500'
      }
      return map[status] || 'bg-gray-500/20 text-gray-400'
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatPlanType(plan) {
      return plan ? plan.replace(/_/g, ' ') : '-'
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = code
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      }
    }
  }
}
</script>